import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { smallcapsBold13Styles } from "@constants/typography"
import { Link } from "gatsby"
import React from "react"
import styled from "styled-components"

const Container = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
`

const lineBaseProps = `
  position: absolute;
  bottom: 0;
  width: 50%;
  height: 2px;
  transition: transform 150ms ${cubicBezier};
  transform-origin: bottom;
  background-color: ${colors.black};
`

const LeftLine = styled.div`
  ${lineBaseProps}
  right: 50%;
  transform-origin: bottom right;
  transform: translateX(12px) scaleY(0);

  @media ${breakpoints.desktop} {
    transform: translateX(24px) scaleY(0);
  }
`

const RightLine = styled.div`
  ${lineBaseProps}
  left: 50%;
  transform-origin: bottom left;
  transform: translateX(-12px) scaleY(0);

  @media ${breakpoints.desktop} {
    transform: translateX(-24px) scaleY(0);
  }
`

const Button = styled.button<{ $active: boolean }>`
  position: relative;
  height: 100%;
  padding: 2px 8px 0;
  flex-shrink: 0;
  will-change: background-color;
  transition: background-color ${animation.medium} ${cubicBezier};
  background: none;
  border: none;
  outline: none;

  &:active {
    outline: none;
  }

  &:focus {
    outline: none;
  }

  @media ${breakpoints.laptop} {
    &:hover {
      background-color: ${colors.dividerLineGray};
      cursor: pointer;
    }
  }

  @media ${breakpoints.desktop} {
    padding: 2px 16px 0;
  }

  ${({ $active }) =>
    $active &&
    `
    ${LeftLine} {
      transform-origin: right;
      transform: translateX(12px) scaleY(1);
    }

    ${RightLine} {
      transform-origin: left;
      transform: translateX(-12px) scaleY(1);
    }

    &:hover {
      ${LeftLine}, ${RightLine} {
        transform: translateX(0) scaleY(1);
      }
    }

    @media ${breakpoints.desktop} {
      ${LeftLine} {
        transform: translateX(24px) scaleY(1);
      }

      ${RightLine} {
        transform: translateX(-24px) scaleY(1);
      }

      &:hover {
        ${LeftLine}, ${RightLine} {
          transform: translateX(0) scaleY(1);
        }
      }
    }
  `}
`

const Label = styled.span<{ $faded: boolean }>`
  ${smallcapsBold13Styles}
  padding-top: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $faded }) => ($faded ? colors.mediumGray : colors.black)};

  @media ${breakpoints.mediumLaptop} {
    font-size: 12px;
    white-space: nowrap;
  }

  @media ${breakpoints.largeLaptop} {
    font-size: 13px;
    font-weight: 400;
  }
`

interface MegaMenuHeaderProps {
  active?: string
  headerRef?: any
  items: Array<{
    label: string
    link?: string
    onClick?: () => void
    slug: string
  }>
}

const MegaMenuHeader: React.FC<MegaMenuHeaderProps> = ({
  active,
  headerRef,
  items,
}) => {
  return (
    <Container ref={headerRef ? headerRef : undefined}>
      {items.map(({ label, link, onClick, slug }) =>
        link ? (
          <Link key={`megamenu-${slug}`} to={link}>
            <Button $active={active === slug}>
              <Label $faded={!!active && active !== slug}>{label}</Label>
              <LeftLine />
              <RightLine />
            </Button>
          </Link>
        ) : (
          <Button
            $active={active === slug}
            key={`megamenu-${slug}`}
            onClick={onClick}
          >
            <Label $faded={!!active && active !== slug}>{label}</Label>
            <LeftLine />
            <RightLine />
          </Button>
        )
      )}
    </Container>
  )
}

export default MegaMenuHeader
